import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { message } from 'antd';
import manufacturingService, { Shift, CalendarDay, Workstation, WorkstationImportSummary, CapacityPlan, JobSchedule } from '../services/manufacturing.service';
import { getCompanyIdFromStorage } from '../utils/jwtPermissions';

const getCompanyId = (): string => getCompanyIdFromStorage();

// ==================== Shifts Hooks ====================
export const useShifts = () => {
  const company_id = getCompanyId();
  
  return useQuery({
    queryKey: ['shifts', company_id],
    queryFn: async () => {
      const response = await manufacturingService.shifts.getAll(company_id);
      return response.data.data || response.data;
    },
    enabled: !!company_id,
  });
};

export const useActiveShifts = () => {
  const company_id = getCompanyId();
  
  return useQuery({
    queryKey: ['shifts', 'active', company_id],
    queryFn: async () => {
      const response = await manufacturingService.shifts.getActive(company_id);
      return response.data.data || response.data;
    },
    enabled: !!company_id,
  });
};

export const useCreateShift = () => {
  const queryClient = useQueryClient();
  const company_id = getCompanyId();
  
  return useMutation({
    mutationFn: (data: Shift) => manufacturingService.shifts.create({ ...data, company_id }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['shifts'] });
      message.success('Shift created successfully');
    },
    onError: (error: any) => {
      message.error(error.response?.data?.message || 'Failed to create shift');
    },
  });
};

export const useUpdateShift = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<Shift> }) => 
      manufacturingService.shifts.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['shifts'] });
      message.success('Shift updated successfully');
    },
    onError: (error: any) => {
      message.error(error.response?.data?.message || 'Failed to update shift');
    },
  });
};

export const useDeleteShift = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (id: string) => manufacturingService.shifts.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['shifts'] });
      message.success('Shift deleted successfully');
    },
    onError: (error: any) => {
      message.error(error.response?.data?.message || 'Failed to delete shift');
    },
  });
};

// ==================== Calendar Hooks ====================
export const useCalendar = (start_date: string, end_date: string) => {
  const company_id = getCompanyId();
  
  return useQuery({
    queryKey: ['calendar', company_id, start_date, end_date],
    queryFn: async () => {
      const response = await manufacturingService.calendar.getCalendar(company_id, start_date, end_date);
      return response.data.data || response.data;
    },
    enabled: !!company_id && !!start_date && !!end_date,
  });
};

export const useHolidays = (year: number) => {
  const company_id = getCompanyId();
  
  return useQuery({
    queryKey: ['holidays', company_id, year],
    queryFn: async () => {
      const response = await manufacturingService.calendar.getHolidays(company_id, year);
      return response.data.data || response.data;
    },
    enabled: !!company_id && !!year,
  });
};

export const useMarkHoliday = () => {
  const queryClient = useQueryClient();
  const company_id = getCompanyId();
  
  return useMutation({
    mutationFn: (data: {
      date: string;
      holiday_name: string;
      holiday_type: string;
      description?: string;
    }) => manufacturingService.calendar.markHoliday({ ...data, company_id }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['calendar'] });
      queryClient.invalidateQueries({ queryKey: ['holidays'] });
      message.success('Holiday marked successfully');
    },
    onError: (error: any) => {
      message.error(error.response?.data?.message || 'Failed to mark holiday');
    },
  });
};

// ==================== Workstations Hooks ====================
export const useWorkstations = () => {
  const company_id = getCompanyId();
  
  return useQuery({
    queryKey: ['workstations', company_id],
    queryFn: async () => {
      const response = await manufacturingService.workstations.getAll(company_id);
      return response.data.data || response.data;
    },
    enabled: !!company_id,
  });
};

export const useAvailableWorkstations = () => {
  const company_id = getCompanyId();
  
  return useQuery({
    queryKey: ['workstations', 'available', company_id],
    queryFn: async () => {
      const response = await manufacturingService.workstations.getAvailable(company_id);
      return response.data.data || response.data;
    },
    enabled: !!company_id,
  });
};

export const useMaintenanceDue = () => {
  const company_id = getCompanyId();
  
  return useQuery({
    queryKey: ['workstations', 'maintenance-due', company_id],
    queryFn: async () => {
      const response = await manufacturingService.workstations.getMaintenanceDue(company_id);
      return response.data.data || response.data;
    },
    enabled: !!company_id,
  });
};

export const useCreateWorkstation = () => {
  const queryClient = useQueryClient();
  const company_id = getCompanyId();
  
  return useMutation({
    mutationFn: (data: Workstation) => manufacturingService.workstations.create({ ...data, company_id }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['workstations'] });
      message.success('Workstation created successfully');
    },
    onError: (error: any) => {
      message.error(error.response?.data?.message || 'Failed to create workstation');
    },
  });
};

export const useUpdateWorkstation = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<Workstation> }) => 
      manufacturingService.workstations.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['workstations'] });
      message.success('Workstation updated successfully');
    },
    onError: (error: any) => {
      message.error(error.response?.data?.message || 'Failed to update workstation');
    },
  });
};

export const useDeleteWorkstation = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (id: string) => manufacturingService.workstations.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['workstations'] });
      message.success('Workstation deleted successfully');
    },
    onError: (error: any) => {
      message.error(error.response?.data?.message || 'Failed to delete workstation');
    },
  });
};

export const useDownloadWorkstationImportTemplate = () => {
  return useMutation({
    mutationFn: () => manufacturingService.workstations.downloadImportTemplate(),
    onSuccess: (response) => {
      const blob = new Blob([response.data], { type: 'text/csv;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'workstation_import_template.csv';
      a.click();
      URL.revokeObjectURL(url);
      message.success('Template downloaded');
    },
    onError: (error: any) => {
      message.error(error.response?.data?.message || 'Failed to download template');
    },
  });
};

export const useImportWorkstationsCsv = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (variables: { file: File; updateExisting?: boolean }) => {
      const response = await manufacturingService.workstations.importCsv(variables.file, {
        updateExisting: variables.updateExisting,
      });
      return (response.data?.data ?? response.data) as WorkstationImportSummary;
    },
    onSuccess: (summary) => {
      queryClient.invalidateQueries({ queryKey: ['workstations'] });
      message.success(
        `Import complete: ${summary?.created ?? 0} created, ${summary?.updated ?? 0} updated, ${summary?.skipped ?? 0} skipped, ${summary?.failed ?? 0} row errors`
      );
    },
    onError: (error: any) => {
      message.error(error.response?.data?.message || 'Import failed');
    },
  });
};

// ==================== Capacity Planning Hooks ====================
export const useCapacityPlan = (workstation_id: string, start_date: string, end_date: string) => {
  const company_id = getCompanyId();
  
  return useQuery({
    queryKey: ['capacity', company_id, workstation_id || 'all', start_date, end_date],
    queryFn: async () => {
      // Fetch capacity plans - backend will handle filtering by workstation
      const wsId = (!workstation_id || workstation_id === 'all') ? '' : workstation_id;
      const response = await manufacturingService.capacity.getPlan(company_id, wsId, start_date, end_date);
      return response.data.data || response.data;
    },
    enabled: !!company_id && !!start_date && !!end_date,
  });
};

export const useGenerateCapacity = () => {
  const queryClient = useQueryClient();
  const company_id = getCompanyId();
  return useMutation({
    mutationFn: (variables: { workstation_id: string; start_date: string; end_date: string }) =>
      manufacturingService.capacity.generate(company_id, variables.workstation_id, variables.start_date, variables.end_date),
    onSuccess: (response: { data?: unknown }) => {
      queryClient.invalidateQueries({ queryKey: ['capacity'] });
      const rows = response?.data;
      const count = Array.isArray(rows) ? rows.length : 0;
      if (count > 0) {
        message.success(`Capacity buckets generated from the HRMS calendar (${count}). Actual hours were loaded from end-of-day close and work order operations.`);
      } else {
        message.warning('No capacity buckets for this range. Check the HRMS work week and that a manufacturing shift covers those weekdays.');
      }
    },
    onError: (error: any) => {
      message.error(error.response?.data?.message || error.message || 'Could not generate capacity');
    },
  });
};

export const useLoadOpenWork = () => {
  const queryClient = useQueryClient();
  const company_id = getCompanyId();
  return useMutation({
    mutationFn: async (variables: { start_date: string; end_date: string }) => {
      const response = await manufacturingService.capacity.loadOpenWork(company_id, variables.start_date, variables.end_date);
        return (response.data?.data ?? response.data) as {
        planned_hours: number;
        unassigned_hours: number;
        unbucketed_hours: number;
        overloaded_buckets: number;
        actual_hours: number;
        actual_unassigned_hours: number;
        actual_unbucketed_hours: number;
      };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['capacity'] });
    },
    onError: (error: any) => {
      message.error(error.response?.data?.message || error.message || 'Could not load open work');
    },
  });
};

export const useBottlenecks = (start_date: string, end_date: string) => {
  const company_id = getCompanyId();
  
  return useQuery({
    queryKey: ['capacity', 'bottlenecks', company_id, start_date, end_date],
    queryFn: async () => {
      const response = await manufacturingService.capacity.identifyBottlenecks(company_id, start_date, end_date);
      return response.data.data || response.data;
    },
    enabled: !!company_id && !!start_date && !!end_date,
  });
};

// ==================== Job Scheduling Hooks ====================
export const useSchedules = () => {
  const company_id = getCompanyId();
  
  return useQuery({
    queryKey: ['schedules', company_id],
    queryFn: async () => {
      const response = await manufacturingService.schedules.getAll(company_id);
      return response.data.data || response.data;
    },
    enabled: !!company_id,
  });
};

export const useScheduleSummary = (start_date: string, end_date: string) => {
  const company_id = getCompanyId();
  
  return useQuery({
    queryKey: ['schedules', 'summary', company_id, start_date, end_date],
    queryFn: async () => {
      const response = await manufacturingService.schedules.getSummary(company_id, start_date, end_date);
      return response.data.data || response.data;
    },
    enabled: !!company_id && !!start_date && !!end_date,
  });
};

export const useAutoSchedule = () => {
  const queryClient = useQueryClient();
  const company_id = getCompanyId();
  
  return useMutation({
    mutationFn: (work_order_ids: string[]) => 
      manufacturingService.schedules.autoSchedule(company_id, work_order_ids),
    onSuccess: (response) => {
      queryClient.invalidateQueries({ queryKey: ['schedules'] });
      const result = response.data.data || response.data;
      message.success(`Auto-scheduled ${result.scheduled?.length || 0} work orders`);
      if (result.conflicts && result.conflicts.length > 0) {
        message.warning(`${result.conflicts.length} work orders could not be scheduled`);
      }
    },
    onError: (error: any) => {
      message.error(error.response?.data?.message || 'Auto-scheduling failed');
    },
  });
};

export const useStartJob = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (id: string) => manufacturingService.schedules.startJob(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['schedules'] });
      message.success('Job started successfully');
    },
    onError: (error: any) => {
      message.error(error.response?.data?.message || 'Failed to start job');
    },
  });
};

export const useCompleteJob = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({
      id,
      produced_quantity,
      rejected_quantity,
      rework_quantity,
      finished_serial_or_tag,
      finished_item_id,
    }: {
      id: string;
      produced_quantity: number;
      rejected_quantity?: number;
      rework_quantity?: number;
      finished_serial_or_tag?: string;
      finished_item_id?: string;
    }) =>
      manufacturingService.schedules.completeJob(id, produced_quantity, rejected_quantity, rework_quantity, {
        finished_serial_or_tag,
        finished_item_id,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['schedules'] });
      queryClient.invalidateQueries({ queryKey: ['items', 'stock'] });
      queryClient.invalidateQueries({ queryKey: ['traceability-matrix'] });
      queryClient.invalidateQueries({ queryKey: ['traceability-forward'] });
      queryClient.invalidateQueries({ queryKey: ['traceability-backward'] });
      message.success('Job completed successfully');
    },
    onError: (error: any) => {
      message.error(error.response?.data?.message || 'Failed to complete job');
    },
  });
};

export const useCreateSchedule = () => {
  const queryClient = useQueryClient();
  const company_id = getCompanyId();
  
  return useMutation({
    mutationFn: (data: JobSchedule) => manufacturingService.schedules.create({ ...data, company_id }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['schedules'] });
      message.success('Schedule created successfully');
    },
    onError: (error: any) => {
      message.error(error.response?.data?.message || 'Failed to create schedule');
    },
  });
};

export const useUpdateSchedule = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<JobSchedule> }) => 
      manufacturingService.schedules.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['schedules'] });
      message.success('Schedule updated successfully');
    },
    onError: (error: any) => {
      message.error(error.response?.data?.message || 'Failed to update schedule');
    },
  });
};

export const useDeleteSchedule = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (id: string) => manufacturingService.schedules.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['schedules'] });
      message.success('Schedule deleted successfully');
    },
    onError: (error: any) => {
      message.error(error.response?.data?.message || 'Failed to delete schedule');
    },
  });
};

